/**
 * Clean up what a speech recogniser returned. Whisper-style models invent text on silence or room noise: a stock phrase from
 * their training subtitles ("Thanks for watching", "اشتركوا في القناة", "ترجمة نانسي قنقر") or a word repeated over and over.
 * Sending that to the assistant makes it answer something the user never said.
 *
 * Deliberately conservative: a stock phrase is only dropped from a SHORT utterance (≤ 8 words) because real long speech
 * that happens to contain "subscribe" must pass; a bare "thank you" is a real thing to say and is never dropped.
 */
const SPAM: RegExp[] = [
  /subtitles?\s+(by|from)\b/i,
  /amara\.org/i,
  /thanks?\s+for\s+watching/i,
  /thank\s+you\s+for\s+(watching|listening)/i,
  /\bsubscribe\b.*\bchannel\b|\bplease\s+subscribe\b|\blike\s+and\s+subscribe\b/i,
  /see\s+you\s+(in\s+)?(the\s+)?next\s+(video|time|episode)/i,
  /^\W*[[(]?\s*(music|applause|laughter|silence|inaudible|blank_audio)\s*[\])]?\W*$/i,
  /^[♪♫♬\s.]+$/u, // note: \W is ASCII-only in JavaScript and would match any Arabic text
  /ترجمة\s+نانسي\s+قنقر/,
  /ترجمة\s+(و)?(تدقيق|مراجعة)/,
  /اشترك(وا)?\s+(في|ب)\s*(ال)?قناة/,
  /لا\s+تنس(وا)?\s+(ال)?(إ|ا)?شتراك/,
  /شكر(ا|اً)\s+(على|ل)\s*(ال)?مشاهدة/,
  /شكر(ا|اً)\s+لمشاهدتكم/,
  /تابعونا\s+على/,
];

const words = (s: string) => s.split(/\s+/).filter(Boolean);

/** The text to use, or "" when the recogniser only hallucinated. */
export function cleanTranscript(raw: string): string {
  let t = raw
    .replace(/[​-‍﻿]/g, "") // zero-width characters
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  const w = words(t);
  if (w.length <= 8 && SPAM.some((r) => r.test(t))) return "";
  // A single token looped (the classic degenerate output): nothing real was said.
  const norm = w.map((x) => x.toLowerCase().replace(/[^\p{L}\p{N}]/gu, ""));
  if (w.length >= 8 && new Set(norm).size === 1) return "";
  // Long runs of the same word inside real speech: keep two.
  t = t.replace(/(^|\s)(\S+)(?:\s+\2){4,}(?=\s|$)/g, "$1$2 $2");
  return t.trim();
}
